import { useEffect, useState } from "react";
import { CreditCard, Clock, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, errMsg, inr, fmtTime } from "@/lib/api";

function loadRazorpay() {
  return new Promise((res) => {
    if (window.Razorpay) return res(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => res(true);
    s.onerror = () => res(false);
    document.body.appendChild(s);
  });
}

export default function PaymentPanel({ appt, onDone }) {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/payments/config").then((r) => setCfg(r.data)); }, []);

  const payOnline = async () => {
    setBusy(true);
    try {
      if (!(await loadRazorpay())) throw new Error("Could not load Razorpay checkout. Check your connection.");
      const { data: o } = await api.post("/payments/order", { appointment_id: appt.id });
      const rzp = new window.Razorpay({
        key: o.key_id, order_id: o.order_id, amount: o.amount, currency: o.currency, name: o.name, description: o.description, prefill: o.prefill,
        theme: { color: "#086B2D" },
        handler: async (resp) => {
          try { await api.post("/payments/verify", resp); toast.success("Payment verified. Consultation confirmed."); onDone(); }
          catch (e) { toast.error(errMsg(e)); }
        },
        modal: { ondismiss: () => setBusy(false) },
      });
      rzp.on("payment.failed", (r) => { api.post("/payments/failed", { order_id: o.order_id, reason: r.error?.description || "" }); toast.error("Payment failed. You can try again."); setBusy(false); });
      rzp.open();
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };

  if (!cfg) return null;
  return (
    <div className="surface p-5 border-[#FF9700]/40" data-testid="payment-panel">
      <div className="flex items-center gap-2 text-[#9A5B00] text-sm font-medium"><Clock className="h-4 w-4" /> Slot held until {fmtTime(appt.hold_expires_at)} IST</div>
      <div className="mt-4 space-y-1 text-sm">
        <div className="flex justify-between"><span className="muted">{appt.co_doctor_id ? "Clinician fee" : "Consultation fee"}</span><span>{inr(appt.fee)}</span></div>
        {appt.co_doctor_id && <div className="flex justify-between" data-testid="payment-co-fee"><span className="muted">Diagnostician fee</span><span>{inr(appt.co_fee)}</span></div>}
        <div className="flex justify-between"><span className="muted">Platform fee</span><span>{inr(appt.platform_fee)}</span></div>
        <div className="flex justify-between font-semibold pt-2 border-t border-[#E2E8ED]"><span>Total payable</span><span data-testid="payment-total">{inr(appt.total)}</span></div>
      </div>
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        <Button className="rounded-full flex-1" disabled={busy || !cfg.configured} onClick={payOnline} data-testid="razorpay-pay-button">
          <CreditCard className="h-4 w-4 mr-2" /> Pay {inr(appt.total)} with Razorpay
        </Button>
      </div>
      {!cfg.configured && (
        <p className="mt-3 flex gap-2 text-xs text-[#9A5B00]" data-testid="payment-unconfigured-note"><AlertTriangle className="h-4 w-4 shrink-0" />
          Online payment is not yet activated (Razorpay keys pending).</p>
      )}
    </div>
  );
}
